import React, { useState, useCallback } from 'react';

type ConflictInfo = {
  path: string;
  localContent: string;
  externalContent: string;
  localTimestamp: number;
  externalTimestamp: number;
};

type ConflictResolutionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  conflicts: ConflictInfo[];
  onResolve: (path: string, keepLocal: boolean) => void;
  onResolveAll: (keepLocal: boolean) => void;
};

export const ConflictResolutionModal = React.memo(function ConflictResolutionModal({
  isOpen,
  onClose,
  conflicts,
  onResolve,
  onResolveAll,
}: ConflictResolutionModalProps) {
  const [selectedConflict, setSelectedConflict] = useState<ConflictInfo | null>(
    conflicts[0] || null
  );
  const [viewMode, setViewMode] = useState<'side-by-side' | 'unified'>('side-by-side');

  const formatTime = useCallback((timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  }, []);

  const handleResolve = useCallback((keepLocal: boolean) => {
    if (selectedConflict) {
      onResolve(selectedConflict.path, keepLocal);
      // Select next conflict
      const remainingConflicts = conflicts.filter((c) => c.path !== selectedConflict.path);
      setSelectedConflict(remainingConflicts[0] || null);
    }
  }, [selectedConflict, onResolve, conflicts]);

  const handleResolveAll = useCallback((keepLocal: boolean) => {
    onResolveAll(keepLocal);
    onClose();
  }, [onResolveAll, onClose]);

  React.useEffect(() => {
    if (conflicts.length > 0 && !selectedConflict) {
      setSelectedConflict(conflicts[0]);
    }
  }, [conflicts, selectedConflict]);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '900px',
          width: '90vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="modal-header">
          <div className="modal-title">
            Conflict Resolution ({conflicts.length} file{conflicts.length !== 1 ? 's' : ''})
          </div>
          <button className="modal-close" onClick={onClose}>
            x
          </button>
        </div>

        <div className="modal-body" style={{ padding: '0', flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* File list sidebar */}
          <div
            style={{
              width: '200px',
              borderRight: '1px solid var(--border)',
              overflow: 'auto',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                padding: '8px 12px',
                fontSize: '11px',
                fontWeight: 'bold',
                color: 'var(--muted)',
                borderBottom: '1px solid var(--border)',
                background: 'var(--bg-secondary)',
              }}
            >
              Changed Files
            </div>
            {conflicts.map((conflict) => (
              <div
                key={conflict.path}
                onClick={() => setSelectedConflict(conflict)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  background: selectedConflict?.path === conflict.path ? 'var(--accent)' : 'transparent',
                  color: selectedConflict?.path === conflict.path ? 'white' : 'var(--text)',
                  fontSize: '12px',
                  borderBottom: '1px solid var(--border)',
                  wordBreak: 'break-all',
                }}
              >
                <div style={{ fontWeight: 500 }}>
                  {conflict.path.split('/').pop()}
                </div>
                <div
                  style={{
                    fontSize: '10px',
                    opacity: 0.7,
                    marginTop: '2px',
                  }}
                >
                  {conflict.externalContent ? 'Modified' : 'Deleted externally'}
                </div>
              </div>
            ))}
          </div>

          {/* Content area */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {selectedConflict ? (
              <>
                {/* Toolbar */}
                <div
                  style={{
                    padding: '8px 12px',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    background: 'var(--bg-secondary)',
                  }}
                >
                  <span style={{ fontWeight: 'bold', fontSize: '13px' }}>
                    {selectedConflict.path}
                  </span>
                  <div style={{ flex: 1 }} />
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      className={`pill-btn ${viewMode === 'side-by-side' ? 'active' : ''}`}
                      onClick={() => setViewMode('side-by-side')}
                      style={{
                        background: viewMode === 'side-by-side' ? 'var(--accent)' : 'var(--bg)',
                        color: viewMode === 'side-by-side' ? 'white' : 'var(--text)',
                        fontSize: '11px',
                      }}
                    >
                      Side by Side
                    </button>
                    <button
                      className={`pill-btn ${viewMode === 'unified' ? 'active' : ''}`}
                      onClick={() => setViewMode('unified')}
                      style={{
                        background: viewMode === 'unified' ? 'var(--accent)' : 'var(--bg)',
                        color: viewMode === 'unified' ? 'white' : 'var(--text)',
                        fontSize: '11px',
                      }}
                    >
                      Unified
                    </button>
                  </div>
                </div>

                {/* Diff view */}
                <div style={{ flex: 1, overflow: 'auto' }}>
                  {viewMode === 'side-by-side' ? (
                    <div style={{ display: 'flex', height: '100%' }}>
                      {/* Local version */}
                      <div style={{ flex: 1, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column' }}>
                        <div
                          style={{
                            padding: '8px 12px',
                            background: 'var(--bg-secondary)',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            color: 'var(--success)',
                            borderBottom: '1px solid var(--border)',
                          }}
                        >
                          Your Version (Local)
                          <span style={{ fontWeight: 'normal', marginLeft: '8px', opacity: 0.7 }}>
                            {formatTime(selectedConflict.localTimestamp)}
                          </span>
                        </div>
                        <pre
                          style={{
                            flex: 1,
                            margin: 0,
                            padding: '12px',
                            fontSize: '12px',
                            fontFamily: 'monospace',
                            overflow: 'auto',
                            background: 'var(--bg)',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {selectedConflict.localContent || '(empty)'}
                        </pre>
                      </div>

                      {/* External version */}
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div
                          style={{
                            padding: '8px 12px',
                            background: 'var(--bg-secondary)',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            color: 'var(--warning)',
                            borderBottom: '1px solid var(--border)',
                          }}
                        >
                          External Version
                          <span style={{ fontWeight: 'normal', marginLeft: '8px', opacity: 0.7 }}>
                            {formatTime(selectedConflict.externalTimestamp)}
                          </span>
                        </div>
                        <pre
                          style={{
                            flex: 1,
                            margin: 0,
                            padding: '12px',
                            fontSize: '12px',
                            fontFamily: 'monospace',
                            overflow: 'auto',
                            background: 'var(--bg)',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {selectedConflict.externalContent || '(deleted)'}
                        </pre>
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: '12px' }}>
                      <div
                        style={{
                          marginBottom: '12px',
                          padding: '8px 12px',
                          background: 'rgba(76, 175, 80, 0.1)',
                          border: '1px solid var(--success)',
                          borderRadius: '4px',
                          fontSize: '11px',
                        }}
                      >
                        <strong style={{ color: 'var(--success)' }}>- Your Version</strong>
                        <span style={{ marginLeft: '8px', opacity: 0.7 }}>
                          {formatTime(selectedConflict.localTimestamp)}
                        </span>
                      </div>
                      <pre
                        style={{
                          margin: '0 0 16px 0',
                          padding: '12px',
                          fontSize: '12px',
                          fontFamily: 'monospace',
                          overflow: 'auto',
                          background: 'rgba(76, 175, 80, 0.05)',
                          border: '1px solid var(--border)',
                          borderRadius: '4px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {selectedConflict.localContent || '(empty)'}
                      </pre>

                      <div
                        style={{
                          marginBottom: '12px',
                          padding: '8px 12px',
                          background: 'rgba(255, 152, 0, 0.1)',
                          border: '1px solid var(--warning)',
                          borderRadius: '4px',
                          fontSize: '11px',
                        }}
                      >
                        <strong style={{ color: 'var(--warning)' }}>+ External Version</strong>
                        <span style={{ marginLeft: '8px', opacity: 0.7 }}>
                          {formatTime(selectedConflict.externalTimestamp)}
                        </span>
                      </div>
                      <pre
                        style={{
                          margin: 0,
                          padding: '12px',
                          fontSize: '12px',
                          fontFamily: 'monospace',
                          overflow: 'auto',
                          background: 'rgba(255, 152, 0, 0.05)',
                          border: '1px solid var(--border)',
                          borderRadius: '4px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {selectedConflict.externalContent || '(deleted)'}
                      </pre>
                    </div>
                  )}
                </div>

                {/* Action buttons for selected file */}
                <div
                  style={{
                    padding: '12px',
                    borderTop: '1px solid var(--border)',
                    display: 'flex',
                    gap: '8px',
                    background: 'var(--bg-secondary)',
                  }}
                >
                  <button
                    className="pill-btn"
                    onClick={() => handleResolve(true)}
                    style={{
                      background: 'var(--success)',
                      color: 'white',
                      fontSize: '12px',
                    }}
                  >
                    Keep My Version
                  </button>
                  <button
                    className="pill-btn"
                    onClick={() => handleResolve(false)}
                    style={{
                      background: 'var(--warning)',
                      color: 'white',
                      fontSize: '12px',
                    }}
                  >
                    Use External Version
                  </button>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: '11px', color: 'var(--muted)', alignSelf: 'center' }}>
                    {conflicts.length} conflict{conflicts.length !== 1 ? 's' : ''} remaining
                  </span>
                </div>
              </>
            ) : (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--muted)',
                }}
              >
                No conflicts to resolve
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="pill-btn"
            onClick={() => handleResolveAll(true)}
            style={{ background: 'var(--success)', color: 'white' }}
          >
            Keep All My Versions
          </button>
          <button
            className="pill-btn"
            onClick={() => handleResolveAll(false)}
            style={{ background: 'var(--warning)', color: 'white' }}
          >
            Use All External Versions
          </button>
          <div style={{ flex: 1 }} />
          <button className="pill-btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
});

export default ConflictResolutionModal;
